import React, { useEffect, useState } from "react";
import AdminLayout from "@/components/AdminLayout";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Save, Plus, X } from "lucide-react";
import { toast } from "sonner";

const Field = ({ label, value, onChange, multiline, rows = 2, testid, placeholder }) => (
  <div>
    <label className="text-[10px] uppercase tracking-widest text-[#5C6359] block mb-1">{label}</label>
    {multiline ? (
      <textarea rows={rows} value={value ?? ""} onChange={(e) => onChange(e.target.value)} data-testid={testid} placeholder={placeholder}
        className="w-full bg-transparent border border-[#D1C7B8] focus:border-[#C05C42] rounded-xl p-3 outline-none text-[#2D322B] text-sm" />
    ) : (
      <input value={value ?? ""} onChange={(e) => onChange(e.target.value)} data-testid={testid} placeholder={placeholder}
        className="w-full bg-transparent border-b border-[#D1C7B8] focus:border-[#C05C42] py-2 outline-none text-[#2D322B] text-sm" />
    )}
  </div>
);

function ProtocolEditor({ protocol, onSaved }) {
  const [p, setP] = useState(protocol);
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setP((prev) => ({ ...prev, [k]: v }));

  const setPractice = (i, v) => set("practices", p.practices.map((x, idx) => (idx === i ? v : x)));
  const addPractice = () => set("practices", [...(p.practices || []), ""]);
  const removePractice = (i) => set("practices", p.practices.filter((_, idx) => idx !== i));

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        title: p.title,
        tradition: p.tradition,
        summary: p.summary,
        duration: p.duration,
        practices: (p.practices || []).map((x) => x.trim()).filter(Boolean),
      };
      const { data } = await api.put(`/admin/protocols/${p.id}`, payload);
      setP(data);
      onSaved && onSaved(data);
      toast.success(`Saved "${data.title}"`);
    } catch (e) {
      toast.error(e?.response?.status === 403 ? "Admin access required." : "Could not save. Try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-[#F1ECE4] border border-[#D1C7B8] rounded-2xl p-6" data-testid={`protocol-editor-${p.id}`}>
      <div className="flex items-center justify-between mb-4">
        <span className="text-[10px] uppercase tracking-[2px] text-[#9A8F7E]">Goal · {p.id}</span>
        <button onClick={save} disabled={saving} data-testid={`protocol-save-${p.id}`}
          className="btn-primary inline-flex items-center gap-2 disabled:opacity-60 !py-2 !px-4 text-sm">
          <Save className="w-4 h-4" /> {saving ? "Saving…" : "Save"}
        </button>
      </div>

      <div className="space-y-4">
        <Field label="Title" value={p.title} onChange={(v) => set("title", v)} testid={`protocol-title-${p.id}`} />
        <Field label="Tradition / cultural framing" value={p.tradition} onChange={(v) => set("tradition", v)} testid={`protocol-tradition-${p.id}`}
          placeholder="e.g. Gangulu healing × Chronobiology" />
        <Field label="Summary" value={p.summary} onChange={(v) => set("summary", v)} multiline testid={`protocol-summary-${p.id}`} />
        <Field label="Duration" value={p.duration} onChange={(v) => set("duration", v)} testid={`protocol-duration-${p.id}`} />

        <div>
          <label className="text-[10px] uppercase tracking-widest text-[#5C6359] block mb-2">Practices</label>
          <div className="space-y-2">
            {(p.practices || []).map((pr, i) => (
              <div key={i} className="flex items-center gap-2" data-testid={`protocol-practice-${p.id}-${i}`}>
                <input value={pr} onChange={(e) => setPractice(i, e.target.value)} data-testid={`protocol-practice-input-${p.id}-${i}`}
                  className="flex-1 bg-transparent border-b border-[#D1C7B8] focus:border-[#C05C42] py-1.5 outline-none text-[#2D322B] text-sm" />
                <button onClick={() => removePractice(i)} data-testid={`protocol-practice-remove-${p.id}-${i}`}
                  className="text-[#9A8F7E] hover:text-[#C05C42]" aria-label="Remove practice">
                  <X className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
          <button onClick={addPractice} data-testid={`protocol-practice-add-${p.id}`}
            className="mt-3 inline-flex items-center gap-1.5 text-xs uppercase tracking-widest text-[#C05C42] hover:underline">
            <Plus className="w-3.5 h-3.5" /> Add practice
          </button>
        </div>
      </div>
    </div>
  );
}

export default function AdminProtocols() {
  const { user } = useAuth();
  const [protocols, setProtocols] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user?.is_admin) return;
    api.get("/admin/protocols").then((r) => setProtocols(r.data || [])).catch(() => {}).finally(() => setLoading(false));
  }, [user]);

  const onSaved = (updated) => setProtocols((prev) => prev.map((p) => (p.id === updated.id ? updated : p)));

  return (
    <AdminLayout>
      <div className="mb-8" data-testid="admin-protocols">
        <p className="label-eyebrow mb-2">Admin · Protocols</p>
        <h1 className="font-serif text-3xl text-[#2D322B]">Protocol catalog</h1>
        <p className="text-sm text-[#5C6359] mt-2 max-w-2xl">
          Edit the goal-based protocols members receive. Changes apply live to every member.
          Defaults centre Gangulu / First Nations Australia framing — fine-tune the wording, practices and cultural attributions here.
        </p>
      </div>

      {loading ? (
        <p className="text-[#5C6359]">Loading…</p>
      ) : protocols.length === 0 ? (
        <p className="text-[#5C6359]">No protocols found.</p>
      ) : (
        <div className="grid md:grid-cols-2 gap-6">
          {protocols.map((p) => <ProtocolEditor key={p.id} protocol={p} onSaved={onSaved} />)}
        </div>
      )}
    </AdminLayout>
  );
}
